import React from 'react';
import { LucideIcon } from 'lucide-react';
import type { UseFormRegisterReturn } from 'react-hook-form';

/**
 * One labelled field for the account-creation surfaces.
 *
 * /register, /accept-invitation and /join each hand-rolled this markup, which
 * is how they drifted apart: the invitation page ended up on colour classes
 * that generated no CSS, two of them still used raw red-500/red-600 for the
 * error state, and only one rendered a hint. The label, the icon slot, the
 * error styling and the required marker live here now.
 */
export interface AuthFieldProps {
  id: string;
  label: string;
  registration: UseFormRegisterReturn;
  type?: 'text' | 'email' | 'password';
  placeholder?: string;
  required?: boolean;
  /** Small grey note beside the label — e.g. the username character rule. */
  hint?: string;
  /** Optional leading glyph inside the field. */
  icon?: LucideIcon;
  autoComplete?: string;
  /** The message from react-hook-form's formState.errors[name]. */
  error?: string;
}

const AuthField: React.FC<AuthFieldProps> = ({
  id,
  label,
  registration,
  type = 'text',
  placeholder,
  required = false,
  hint,
  icon: Icon,
  autoComplete,
  error,
}) => (
  <div>
    <label
      htmlFor={id}
      className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1"
    >
      {label}
      {required && (
        <span className="text-error-600 dark:text-error-400 ml-0.5" aria-label="required">
          *
        </span>
      )}
      {hint && (
        <span className="ml-2 text-xs font-normal text-text-tertiary dark:text-text-dark-tertiary">
          {hint}
        </span>
      )}
    </label>

    <div className="relative">
      {Icon && (
        <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Icon className="h-4 w-4 text-text-tertiary dark:text-text-dark-tertiary" aria-hidden="true" />
        </span>
      )}
      <input
        id={id}
        type={type}
        placeholder={placeholder}
        autoComplete={autoComplete}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        className={`input ${Icon ? 'pl-9' : ''} ${error ? 'border-error-500' : ''}`}
        {...registration}
      />
    </div>

    {error && (
      <p id={`${id}-error`} className="text-sm text-error-600 dark:text-error-400 mt-1">
        {error}
      </p>
    )}
  </div>
);

export default AuthField;
